import React from 'react';

const iconProps = { width: 28, height: 28, viewBox: '0 0 28 28', fill: 'none', xmlns: 'http://www.w3.org/2000/svg' };

/** Multi-take-profit bracket: stacked exit targets on a price ladder */
export function BracketTargetsIcon(props) {
  return (
    <svg {...iconProps} {...props} aria-hidden>
      <rect x="4" y="5" width="14" height="2.5" rx="1.25" fill="#22C55E" />
      <rect x="4" y="11" width="11" height="2.5" rx="1.25" fill="#3B82F6" />
      <rect x="4" y="17" width="8" height="2.5" rx="1.25" fill="var(--landing-accent)" />
      <path d="M20 6v16" stroke="#64748B" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M17 8h6M17 14h6M17 20h6" stroke="#94A3B8" strokeWidth="1.25" strokeLinecap="round" />
    </svg>
  );
}

/** Repeatable trading workflow: linked steps */
export function TradingWorkflowIcon(props) {
  return (
    <svg
      width={40}
      height={28}
      viewBox="0 0 40 28"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
      aria-hidden
    >
      <circle cx="5.75" cy="17.5" r="4.75" fill="var(--landing-accent)" />
      <circle cx="20" cy="17.5" r="4.75" fill="var(--landing-accent)" />
      <circle cx="34.25" cy="17.5" r="4.75" fill="var(--landing-accent)" />
      <path
        d="M10.5 17.5H15.25M24.75 17.5H29.5"
        stroke="#FFFFFF"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <text x="5.75" y="18.85" textAnchor="middle" fill="#fff" fontSize="5.5" fontWeight="700" fontFamily="system-ui, sans-serif">
        1
      </text>
      <text x="20" y="18.85" textAnchor="middle" fill="#fff" fontSize="5.5" fontWeight="700" fontFamily="system-ui, sans-serif">
        2
      </text>
      <text x="34.25" y="18.85" textAnchor="middle" fill="#fff" fontSize="5.5" fontWeight="700" fontFamily="system-ui, sans-serif">
        3
      </text>
    </svg>
  );
}

/** Position size from entry–stop distance on a price ladder */
export function PositionSizeIcon(props) {
  return (
    <svg {...iconProps} {...props} aria-hidden>
      <path d="M6 4v20" stroke="#64748B" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M9 8h13.5" stroke="var(--landing-accent)" strokeWidth="2.25" strokeLinecap="round" />
      <circle cx="23" cy="8" r="2" fill="var(--landing-accent)" />
      <path d="M9 21h13.5" stroke="var(--landing-accent)" strokeWidth="2.25" strokeLinecap="round" />
      <circle cx="23" cy="21" r="2" fill="var(--landing-accent)" />
      <path d="M11.5 10.5v8" stroke="var(--landing-accent)" strokeWidth="1.6" strokeLinecap="round" />
      <path
        d="M10 12l1.5-1.5 1.5 1.5M10 17l1.5 1.5 1.5-1.5"
        stroke="var(--landing-accent)"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

/** Auto journal: notebook with capture arrow */
export function AutoJournalIcon(props) {
  return (
    <svg {...iconProps} {...props} aria-hidden>
      <rect x="6" y="5" width="13" height="18" rx="2" fill="#E0E7FF" />
      <path d="M9 10h7M9 14h7M9 18h5" stroke="var(--landing-accent)" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="21" cy="9" r="5" fill="#22C55E" />
      <path d="M19.5 9l1.5 1.5 3-3" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** Analytics + rule violations */
export function ViolationsChartIcon(props) {
  return (
    <svg {...iconProps} {...props} aria-hidden>
      <rect x="5" y="14" width="4" height="8" rx="1" fill="var(--landing-accent)" />
      <rect x="11" y="10" width="4" height="12" rx="1" fill="var(--landing-accent)" />
      <rect x="17" y="6" width="4" height="16" rx="1" fill="#3B82F6" />
      <circle cx="21" cy="7" r="4" fill="#EF4444" />
      <path d="M21 5.5v3M19.5 7h3" stroke="#fff" strokeWidth="1.25" strokeLinecap="round" />
    </svg>
  );
}

/** PrecisionAI: behavioral review — flags mistakes in journals/violations, not price calls */
export function PrecisionAIIcon(props) {
  return (
    <svg {...iconProps} {...props} aria-hidden>
      <rect x="4" y="5" width="15" height="19" rx="2" fill="#EDE9FE" />
      <path d="M7.5 10h8M7.5 14h8M7.5 18h5" stroke="var(--landing-accent)" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="20.5" cy="14" r="4.25" fill="#EF4444" />
      <path d="M20.5 12.4v2.8" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="20.5" cy="16.6" r="0.9" fill="#fff" />
      <circle cx="22.5" cy="7" r="4" fill="var(--landing-accent)" />
      <circle cx="21.2" cy="6.2" r="0.7" fill="#fff" />
      <circle cx="23.4" cy="7.8" r="0.55" fill="#fff" />
      <circle cx="22.8" cy="5.4" r="0.45" fill="#C4B5FD" />
    </svg>
  );
}

/** Liquidity / depth ladder heatmap */
export function LiquidityDepthIcon(props) {
  return (
    <svg {...iconProps} {...props} aria-hidden>
      <rect x="5" y="6" width="5" height="3" rx="0.75" fill="#3B82F6" opacity="0.9" />
      <rect x="5" y="10.5" width="8" height="3" rx="0.75" fill="var(--landing-accent)" opacity="0.85" />
      <rect x="5" y="15" width="11" height="3" rx="0.75" fill="var(--landing-accent)" opacity="0.8" />
      <rect x="5" y="19.5" width="6" height="3" rx="0.75" fill="var(--landing-accent)" opacity="0.75" />
      <path d="M19 6v17" stroke="#22C55E" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

/** Trading rules shield */
export function TradingRulesShieldIcon(props) {
  return (
    <svg {...iconProps} {...props} aria-hidden>
      <path d="M14 4l8 3.5v7c0 5-3.5 8.5-8 10.5C9.5 23 6 19.5 6 14.5v-7L14 4z" fill="var(--landing-accent)" />
      <path d="M10.5 14l2.5 2.5 5-5.5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const APPENDIX_ICON_MAP = {
  'bracket-targets': BracketTargetsIcon,
  'position-size': PositionSizeIcon,
  'auto-journal': AutoJournalIcon,
  'violations-chart': ViolationsChartIcon,
  'precision-ai': PrecisionAIIcon,
  'liquidity-depth': LiquidityDepthIcon,
  'trading-rules': TradingRulesShieldIcon,
};

export function LandingAppendixIcon({ iconId, className }) {
  const Icon = APPENDIX_ICON_MAP[iconId];
  if (!Icon) return null;
  return <Icon className={className} />;
}
